/**
 * A circle's radius, set two ways at once: a slider to drag it bigger and smaller while the
 * circle follows on the map, and a box to type it in metres.
 *
 * One value: the slider moves the box, the box moves the slider, and both redraw the circle.
 * The same control sits in the form beside the map and floats over the map while it covers
 * the whole page, where the form cannot be reached — judging a radius against the streets is
 * exactly what the full-screen map is for (#372).
 *
 * The slider's scale is not straight; `radiusAt` says why.
 */

import { useTranslation } from 'react-i18next';

import { RADIUS_MAX, RADIUS_MIN, RADIUS_POSITIONS, positionOf, radiusAt } from './geometry';
import { cx, Measured } from '../../ui';

export function RadiusControl({
  id,
  value,
  onChange,
  floating,
}: {
  /** The number box's id, given by `Field` in the form; none over the map, where it would repeat. */
  id?: string;
  /** Metres. NaN while the box is being retyped. */
  value: number;
  onChange: (metres: number) => void;
  /** Over the map: a compact row, labelled in place of a `Field` above it. */
  floating?: boolean;
}) {
  const { t } = useTranslation();
  const label = t('zones.radius');

  return (
    <span className={cx('flex items-center gap-3', floating ? 'px-2' : 'w-full')}>
      {floating && <span className="text-secondary font-medium text-ink-2">{label}</span>}
      <input
        type="range"
        aria-label={label}
        aria-valuetext={t('zones.radiusValue', { metres: Number.isNaN(value) ? RADIUS_MIN : value })}
        className={cx('accent-brand cursor-pointer', floating ? 'w-56' : 'flex-1 min-w-0')}
        min={0}
        max={RADIUS_POSITIONS}
        step={1}
        value={positionOf(value)}
        onChange={(event) => onChange(radiusAt(event.target.valueAsNumber))}
      />
      <Measured
        id={id}
        aria-label={floating ? label : undefined}
        unit={t('settings.unitMetres')}
        type="number"
        required
        min={RADIUS_MIN}
        max={RADIUS_MAX}
        value={Number.isNaN(value) ? '' : value}
        onChange={(event) => onChange(event.target.valueAsNumber)}
      />
    </span>
  );
}
